import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '@/api/client';
import type { PageResponse, Tenant } from '@/api/types';
import { ArrowLeftRight, X } from 'lucide-react';
import { Select } from '@/components/ui/Select';

function tenantLabel(t: Tenant) {
  return `${t.roomNumber ?? '—'}-${t.bedLabel ?? '—'} · ${t.fullName}`;
}

export function SwapBedPanel({ hostelId, lockedTenant, onClose, onSuccess, bare }: {
  hostelId: string;
  lockedTenant?: Tenant;
  onClose: () => void;
  onSuccess: () => void;
  /** Render as plain content without the card border/header/close icon — for use as a page's main content rather than an inline dismissible panel. */
  bare?: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tenantBId, setTenantBId] = useState('');

  const { data: tenants = [], isLoading } = useQuery<Tenant[]>({
    queryKey: ['active-tenants', hostelId],
    queryFn: () => client
      .get(`/api/tenants?hostelId=${hostelId}&status=ACTIVE&size=500`)
      .then(r => (r as unknown as PageResponse<Tenant>).content),
    enabled: !!hostelId,
  });

  const [tenantAId, setTenantAId] = useState('');
  const resolvedTenantA = lockedTenant ?? tenants.find(x => x.id === tenantAId);
  const resolvedTenantB = tenants.find(x => x.id === tenantBId);

  const otherOptions = tenants.filter(x => x.id !== resolvedTenantA?.id);
  const firstOptions = tenants.filter(x => x.id !== resolvedTenantB?.id);

  const swap = useMutation({
    mutationFn: () => client.post('/api/tenants/swap-beds', {
      tenantAId: resolvedTenantA!.id,
      tenantBId: resolvedTenantB!.id,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['beds'] });
      void qc.invalidateQueries({ queryKey: ['bed-tenant'] });
      void qc.invalidateQueries({ queryKey: ['tenants'] });
      void qc.invalidateQueries({ queryKey: ['active-tenants'] });
      void qc.invalidateQueries({ queryKey: ['rooms'] });
      void qc.invalidateQueries({ queryKey: ['owner-dashboard'] });
      onSuccess();
    },
  });

  const canSwap = !!resolvedTenantA && !!resolvedTenantB && resolvedTenantA.id !== resolvedTenantB.id;
  const resetSelections = () => { setTenantAId(''); setTenantBId(''); };

  return (
    <div className={bare ? '' : 'bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] rounded-xl p-4'}>
      {!bare && (
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[hsl(var(--foreground))] flex items-center gap-2">
            <ArrowLeftRight size={15} className="text-[hsl(var(--primary))]" />
            {t('owner.swapBeds', 'Swap Beds')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-400 py-2">{t('owner.loadingTenants', 'Loading tenants…')}</p>
      ) : otherOptions.length === 0 ? (
        <p className="text-sm text-gray-500 py-2">
          {t('owner.noOtherTenantsToSwap', 'No other active tenants in this hostel to swap with.')}
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.tenantA', 'Tenant A')}</label>
            {lockedTenant ? (
              <div className="border rounded-lg px-3 py-1.5 text-sm bg-gray-100 text-gray-700">
                {tenantLabel(lockedTenant)}
              </div>
            ) : (
              <Select value={tenantAId} onChange={e => setTenantAId(e.target.value)}
                options={[
                  { value: '', label: t('owner.selectTenant', 'Select tenant…') },
                  ...firstOptions.map(x => ({ value: x.id, label: tenantLabel(x) })),
                ]} />
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.tenantB', 'Tenant B')}</label>
            <Select value={tenantBId} onChange={e => setTenantBId(e.target.value)}
              options={[
                { value: '', label: t('owner.selectTenantToSwap', 'Select tenant to swap with…') },
                ...otherOptions.map(x => ({ value: x.id, label: tenantLabel(x) })),
              ]} />
          </div>
        </div>
      )}

      {canSwap && resolvedTenantA && resolvedTenantB && (
        <div className="mt-3 text-sm bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded-lg px-3 py-2 text-gray-700">
          <p><strong>{resolvedTenantA.fullName}</strong>: {resolvedTenantA.roomNumber}-{resolvedTenantA.bedLabel} → {resolvedTenantB.roomNumber}-{resolvedTenantB.bedLabel}</p>
          <p><strong>{resolvedTenantB.fullName}</strong>: {resolvedTenantB.roomNumber}-{resolvedTenantB.bedLabel} → {resolvedTenantA.roomNumber}-{resolvedTenantA.bedLabel}</p>
        </div>
      )}

      {swap.isError && <p className="text-red-600 text-sm mt-2">{(swap.error as Error).message}</p>}

      <div className="flex gap-2 mt-3">
        <button
          onClick={() => swap.mutate()}
          disabled={!canSwap || swap.isPending}
          className="bg-[hsl(var(--primary))] hover:opacity-90 disabled:opacity-50 text-[hsl(var(--primary-foreground))] px-4 py-1.5 rounded-lg text-sm font-medium"
        >
          {swap.isPending ? t('owner.swapping', 'Swapping…') : t('owner.confirmSwap', 'Confirm Swap')}
        </button>
        <button onClick={bare ? resetSelections : onClose} className="border px-4 py-1.5 rounded-lg text-sm">{t('common.cancel')}</button>
      </div>
    </div>
  );
}
